import classNames from 'classnames';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { trackMixpanelEvent } from 'utils/analytics';
import { getIsDemoFarm } from 'utils/general';

import { Typography } from '@/shared/components/atoms';
import { AssetItemProps } from '@/shared/components/molecules/ChooseAsset/ChooseAsset';
import ChooseLocation from '@/shared/components/molecules/ChooseAsset/ChooseLocation';
import TooltipWithClick from '@/shared/components/molecules/TooltipWithClick';
import { demoAsset } from '@/shared/constant/general';
import routes from '@/shared/constant/routes';
import { assetBlacklist } from '@/shared/constant/routesBlacklist';
import { useGetAssetsList } from '@/shared/hooks/apiHooks';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useFreeTrial } from '@/shared/hooks/useFreeTrial';

import DownArrow from '~/images/shared/DownArrow.svg';

/**
 *
 * @logic
 * check if user subscribed to a bundle
 * in case of subscribed then select first farm
 * in case of not subscribed then select demo farm
 */

const ChooseAsset: React.FC<{
  arrowClassNames?: string;
  containerClassNames?: string;
  placement?: 'top' | 'left' | 'right' | 'bottom';
  useBlackList?: boolean;
  demoAssetBg?: string;
}> = ({
  arrowClassNames,
  containerClassNames,
  placement,
  useBlackList,
  demoAssetBg,
}) => {
  const { user } = useAuth();

  const { hasSubscription, canAccessAssets } = useFreeTrial();

  const { data } = useGetAssetsList(!!user?.companyUser && canAccessAssets);

  const [store, setStore] = useStore((store) => store);
  const selectedAsset = store?.asset;
  const isDemoFarm = store?.isDemoFarm;

  const { pathname, push } = useRouter();

  const setSelectedAsset = async (asset: AssetItemProps) => {
    const newAsset = { ...asset, companyTypeId: data?.companyTypeId };
    //  TODO: IMP get latest state from set store function
    if (
      (isDemoFarm && !getIsDemoFarm(asset)) ||
      (!isDemoFarm && getIsDemoFarm(asset))
    ) {
      if (pathname !== routes.home && pathname !== routes.billing)
        await push(routes.home);
    }
    setStore({ asset: newAsset });
  };

  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const [selectedAssetName, setSelectedAssetName] = useState<string>(
    t('chooseAsset')
  );

  useEffect(() => {
    if (!canAccessAssets) {
      trackMixpanelEvent('Demo Mode', user);
      setStore({
        asset: demoAsset,
        assetsList: {
          farms: [demoAsset],
          packhouses: [],
        },
      });
    } else if (data?.entities) {
      // keep the farms list with empty array at first
      const farms: AssetItemProps[] = !hasSubscription ? [demoAsset] : [];
      const packhouses: AssetItemProps[] = [];
      data.entities?.map((item) =>
        item.entityTypeId === 2 ? farms.push(item) : packhouses.push(item)
      );
      setStore({
        assetsList: {
          farms,
          packhouses,
        },
      });

      // set the first one as default
      // Set the first entity as default if selectedAsset is not present or not in the list
      // if the demo asset is selected, set the first entity only if the user is subscribed; otherwise, allow demo asset
      if (
        !selectedAsset ||
        (!data?.entities.find((item) => item.id === selectedAsset.id) &&
          !(isDemoFarm && !hasSubscription))
      ) {
        setSelectedAsset({
          ...data?.entities[0],
          companyTypeId: data.companyTypeId,
        });
      }
      //  INFO: update the asset without needing to logout
      else {
        const updatedAsset = data.entities.find(
          (entity) => entity.id === selectedAsset.id
        );
        if (updatedAsset) setSelectedAsset(updatedAsset);
      }
    }

    if (data?.isCompanyInventoryActive !== undefined)
      setStore({ isCompanyInventoryActive: data.isCompanyInventoryActive });

    if (data?.isCompanyFacialRecognitionActive !== undefined)
      setStore({
        isCompanyFacialRecognitionActive: data.isCompanyFacialRecognitionActive,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, canAccessAssets]);

  useEffect(() => {
    if (selectedAsset) {
      setSelectedAssetName(selectedAsset.name);
    }
  }, [selectedAsset, language]);

  //  INFO: always execute logic and update entities and inventory subscribtion flag, but hide UI from blacklisted routes
  /**
   * if not using useBlackList
   * or using useBlackList and pathname is included
   * then show list
   * else don't show list
   * */
  return !useBlackList ||
    (useBlackList && !assetBlacklist.includes(pathname)) ? (
    <div>
      <TooltipWithClick
        placement={placement ?? 'top'}
        className='mt-5 !rounded-xl'
        wrapperClassName='rtl:left-0 ltr:right-0'
        content={
          <div className='h-full max-h-[394px] min-h-[300px] w-[50vw] scrollbar tablet:w-[358px]'>
            <Typography className='mb-12' weight='semiBold' variant='subtitle'>
              {t('Properties')}
            </Typography>
            <ChooseLocation
              setselectedAsset={setSelectedAsset}
              defaultAsset={selectedAsset || undefined}
              companyTypeId={data?.companyTypeId}
            />
          </div>
        }
      >
        <button
          className={clsx(
            'flex h-36 items-center gap-12 rounded-lg px-16',
            containerClassNames,
            isDemoFarm
              ? demoAssetBg ?? 'bg-tertiary-default'
              : 'border-xs border-outline-default'
          )}
        >
          <span
            className={clsx(
              'text-14 font-semi-bold',
              isDemoFarm && !demoAssetBg ? 'text-white' : 'text-primary-default'
            )}
          >
            {selectedAssetName.length <= 9
              ? selectedAssetName
              : selectedAssetName.slice(0, 9) + '..'}
          </span>
          <span
            className={classNames(
              arrowClassNames,
              isDemoFarm && !demoAssetBg && 'text-white'
            )}
          >
            <DownArrow />
          </span>
        </button>
      </TooltipWithClick>
    </div>
  ) : (
    <></>
  );
};
export default ChooseAsset;
